import React, { useState, useEffect } from 'react';
import { Upload, X, Save, Trash2, Eye, FileText, ArrowLeft, Building, Link, Download } from 'lucide-react';
import { uploadClaimFormToFirebase, deleteClaimFormFromFirebase, getClaimFormsFromFirebase, saveClaimFormToFirebase } from './utils/firebase';

const AddClaimForm = () => {
  const [claimForms, setClaimForms] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    companyName: '',
    logo: null,
    logoPreview: null,
    file: null,
    fileUrl: '',
    uploadType: 'file' // 'file' or 'url'
  });

  useEffect(() => {
    loadClaimForms();
  }, []);

  const loadClaimForms = async () => {
    try {
      const formsData = await getClaimFormsFromFirebase();
      setClaimForms(formsData);
    } catch (error) {
      console.error('Error loading claim forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit for documents
        alert('File size should be less than 10MB');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        file: file,
        fileUrl: '' // Clear URL if file is selected
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName.trim()) {
      alert('Please provide company name');
      return;
    }

    if (formData.uploadType === 'file' && !formData.file) {
      alert('Please provide a file');
      return;
    }

    if (formData.uploadType === 'url' && !formData.fileUrl.trim()) {
      alert('Please provide a file URL');
      return;
    }

    setUploading(true);
    try {
      let logoUrl = '';
      let fileUrl = '';

      // Upload logo if provided
      if (formData.logo) {
        logoUrl = await uploadClaimFormToFirebase(formData.logo, `${formData.companyName}-logo`, 'logos');
      }

      // Upload file or use provided URL
      if (formData.uploadType === 'file' && formData.file) {
        fileUrl = await uploadClaimFormToFirebase(formData.file, `${formData.companyName}-form`, 'forms');
      } else if (formData.uploadType === 'url') {
        fileUrl = formData.fileUrl;
      }

      const claimFormData = {
        companyName: formData.companyName.trim(),
        logoUrl: logoUrl,
        fileUrl: fileUrl,
        fileType: formData.uploadType,
        fileName: formData.file ? formData.file.name : 'External Link',
        createdAt: new Date().toISOString()
      };
      
      await saveClaimFormToFirebase(claimFormData);
      await loadClaimForms();
      
      // Reset form
      setFormData({
        companyName: '',
        logo: null,
        logoPreview: null,
        file: null,
        fileUrl: '',
        uploadType: 'file'
      });
      
      // Reset file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => input.value = '');
      
      alert('Claim form added successfully!');
    } catch (error) {
      console.error('Error uploading claim form:', error);
      alert('Error uploading claim form. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (claimForm) => {
    if (window.confirm(`Are you sure you want to delete ${claimForm.companyName} claim form?`)) {
      try {
        // Delete files from storage if they exist
        if (claimForm.logoUrl && claimForm.logoUrl.includes('firebase')) {
          await deleteClaimFormFromFirebase(claimForm.logoUrl);
        }
        if (claimForm.fileUrl && claimForm.fileUrl.includes('firebase')) {
          await deleteClaimFormFromFirebase(claimForm.fileUrl);
        }
        
        await deleteClaimFormFromFirebase(claimForm.id);
        await loadClaimForms();
        alert('Claim form deleted successfully!');
      } catch (error) {
        console.error('Error deleting claim form:', error);
        alert('Error deleting claim form. Please try again.');
      }
    }
  };

  const clearLogo = () => {
    setFormData(prev => ({ ...prev, logo: null, logoPreview: null }));
    const logoInput = document.querySelector('input[type="file"][accept="image/*"]');
    if (logoInput) logoInput.value = '';
  };

  const clearFile = () => {
    setFormData(prev => ({ ...prev, file: null }));
    const fileInput = document.querySelector('input[type="file"]:not([accept="image/*"])');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-emerald-600 hover:text-emerald-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center">
                  <FileText className="w-8 h-8 mr-3" />
                  Claim Forms Management
                </h1>
                <p className="text-blue-100 mt-2">Add and manage insurance claim forms</p>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <span className="text-white font-semibold">{claimForms.length} Forms</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Upload Form */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 mb-8 border border-gray-200">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
                <Upload className="w-6 h-6 mr-3 text-blue-600" />
                Add New Claim Form
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="Enter company name (e.g., HDFC ERGO)"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Company Logo (Optional - Max 5MB)
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center shadow-lg hover:shadow-xl">
                      <Building className="w-5 h-5 mr-2" />
                      Choose Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoSelect}
                        className="hidden"
                      />
                    </label>
                    {formData.logo && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                          {formData.logo.name}
                        </span>
                        <button
                          type="button"
                          onClick={clearLogo}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-all"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {formData.logoPreview && (
                    <div className="mt-4">
                      <div className="w-20 h-20 bg-white rounded-xl border-2 border-gray-200 p-2 flex items-center justify-center shadow-sm">
                        <img
                          src={formData.logoPreview}
                          alt="Logo Preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Upload Type *
                  </label>
                  <div className="flex space-x-4 mb-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="uploadType"
                        value="file"
                        checked={formData.uploadType === 'file'}
                        onChange={(e) => setFormData(prev => ({ ...prev, uploadType: e.target.value }))}
                        className="mr-2"
                      />
                      Upload File
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="uploadType"
                        value="url"
                        checked={formData.uploadType === 'url'}
                        onChange={(e) => setFormData(prev => ({ ...prev, uploadType: e.target.value }))}
                        className="mr-2"
                      />
                      External URL
                    </label>
                  </div>

                  {formData.uploadType === 'file' ? (
                    <div className="flex items-center space-x-4">
                      <label className="cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center shadow-lg hover:shadow-xl">
                        <FileText className="w-5 h-5 mr-2" />
                        Choose File
                        <input
                          type="file"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                      {formData.file && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
                            {formData.file.name}
                          </span>
                          <button
                            type="button"
                            onClick={clearFile}
                            className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-all"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Link className="w-5 h-5 text-gray-400" />
                      <input
                        type="url"
                        value={formData.fileUrl}
                        onChange={(e) => setFormData(prev => ({ ...prev, fileUrl: e.target.value }))}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                        placeholder="Enter file URL (e.g., https://example.com/form.pdf)"
                      />
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {uploading ? 'Uploading...' : 'Add Claim Form'}
                </button>
              </form>
            </div>

            {/* Claim Forms Grid */}
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center justify-between">
                <span>Current Claim Forms</span>
                <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  {claimForms.length} Total
                </span>
              </h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : claimForms.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {claimForms.map((claimForm) => (
                    <div key={claimForm.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {claimForm.logoUrl ? (
                            <div className="w-12 h-12 bg-gray-50 rounded-lg p-2 flex items-center justify-center border border-gray-100">
                              <img
                                src={claimForm.logoUrl}
                                alt={`${claimForm.companyName} logo`}
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                              <Building className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold text-gray-800">{claimForm.companyName}</h3>
                            <p className="text-xs text-gray-500">{claimForm.fileName}</p>
                          </div>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          claimForm.fileType === 'url' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {claimForm.fileType === 'url' ? 'URL' : 'File'}
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => window.open(claimForm.fileUrl, '_blank')}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => handleDelete(claimForm)}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </button>
                      </div>
                      
                      {claimForm.createdAt && (
                        <div className="text-xs text-gray-500 text-center mt-3">
                          Added: {new Date(claimForm.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-500 mb-2">No Claim Forms Added Yet</h3>
                  <p className="text-gray-400">Add your first claim form using the form above.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClaimForm;
